import {
  faArrowDownToLine,
  faBug,
  faCheckCircle,
  faCopy,
  faPen,
  faTrash,
} from "@fortawesome/pro-regular-svg-icons";

import {
  MENU_DIVIDER,
  OptionsDropdownElement,
} from "src/base-components/OptionsDropdown/OptionsDropdownItems";
import { AgentVersionConfigurationOutput } from "src/clients/agent-management";
import { toastActions } from "src/design-system/Toast/utils";
import { usePowertools } from "src/powertools/hooks";
import { useWsInfo } from "src/powertools/useWsInfo";
import { getAgentLogsInsightsLink } from "src/powertools/utils";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { copyTextToClipboard } from "src/utils/clipboard";

type UseAgentVersionMenuOptionsParams = {
  isDraft: boolean;
  configuration?: AgentVersionConfigurationOutput;
  onEdit?: () => void;
  onDuplicate: () => void;
  onPublish: () => void;
  onDelete?: () => void;
  isPublishing?: boolean;
};

/**
 * Builds the dropdown menu options for an agent version's action menu.
 * Used by both the version detail page (PageMenu) and the version list page (AgentVersionActionsMenu).
 *
 * Regular items: Edit (draft only), Duplicate, Publish (draft only), Delete (draft only).
 * Powertools items (separated by a divider): View Agent Stack Logs, Export config JSON.
 */
export const useAgentVersionMenuOptions = ({
  isDraft,
  configuration,
  onEdit,
  onDuplicate,
  onPublish,
  onDelete,
  isPublishing,
}: UseAgentVersionMenuOptionsParams): OptionsDropdownElement[] => {
  const isPowerToolsEnabled = usePowertools();
  const { workspace } = useWorkspaceContext();
  const wsInfo = useWsInfo(workspace);

  const handleOpenAgentLogs = () => {
    if (wsInfo.data?.stack_name && wsInfo.data?.region) {
      window.open(
        getAgentLogsInsightsLink(wsInfo.data.stack_name, wsInfo.data.region),
        "_blank",
      );
    }
  };

  const handleExportConfig = async () => {
    if (configuration) {
      await copyTextToClipboard(JSON.stringify(configuration, null, 2));
      toastActions.success({ title: "Configuration copied to clipboard" });
    }
  };

  const regularItems: (OptionsDropdownElement | false)[] = [
    !!onEdit &&
      isDraft && {
        key: "Edit version details",
        icon: faPen,
        action: onEdit,
      },
    {
      key: "Duplicate",
      icon: faCopy,
      action: onDuplicate,
    },
    isDraft && {
      key: "Publish Agent",
      icon: faCheckCircle,
      action: onPublish,
      disabled: isPublishing,
    },
    !!onDelete &&
      isDraft && {
        key: "Delete version",
        icon: faTrash,
        action: onDelete,
      },
  ];

  const powertoolsItems: (OptionsDropdownElement | false)[] = [
    !!wsInfo.data?.stack_name &&
      !!wsInfo.data?.region && {
        key: "View Agent Stack Logs",
        icon: faBug,
        action: handleOpenAgentLogs,
      },
    !!configuration && {
      key: "Export config JSON",
      icon: faArrowDownToLine,
      action: handleExportConfig,
    },
  ];

  const filteredRegular = regularItems.filter(
    (item): item is OptionsDropdownElement => !!item,
  );
  const filteredPowertools = powertoolsItems.filter(
    (item): item is OptionsDropdownElement => !!item,
  );

  if (isPowerToolsEnabled && filteredPowertools.length > 0) {
    return [...filteredRegular, MENU_DIVIDER, ...filteredPowertools];
  }

  return filteredRegular;
};
